import { jsPDF } from 'jspdf';
import { applyPlugin } from 'jspdf-autotable';
import { uploadToExternalS3 } from './externalS3Upload';
import { formatDateLong } from './configUtilities.jsx'
import { getCapacityUnit, getThroughputUnit } from './configUtilities';
import { isSoftwareSubscriptionOnly, getVendor, getVendorCapabilities, getVendorTerms } from '../config/vendorRegistry';
import { buildHardwareReferenceRows } from './hardwareReference';
import { calculateNetworkPorts, getNetworkOption } from './networkPorts';
import vduraLogoPng from '../assets/vdura-quote-logo.png';
import { compressImage } from './compressImage';
import { stampPageNumbers, VDURA_FOOTER } from './stampPageNumbers';
import { sanitizeFilenamePart } from './exportFilename';

// jspdf-autotable 5 no longer attaches doc.autoTable on a bare import (it only
// self-applies to a global window.jsPDF, which the ESM import never sets), so
// the plugin is applied explicitly. CONF-237.
applyPlugin(jsPDF);

// Helper function to parse markdown-style bold text and render with jsPDF
// Now with pagination support
const renderFormattedText = (doc, text, startX, startY, maxWidth, maxY = null) => {
  const lineHeight = 3.5;
  let currentY = startY;
  const pageHeight = doc.internal.pageSize.height;
  const bottomMargin = 15;
  const effectiveMaxY = maxY || (pageHeight - bottomMargin);
  
  // Track if we've added continuation pages
  let addedPages = false;
  
  // CONF-216: jsPDF expands a tab to eight spaces when it DRAWS a string
  // (text() → ESC) but measures it as next to nothing in getTextWidth, and
  // the word-wrap below advances X by the measured width of each word it
  // draws. A word carrying a tab ("1.\tContract", every SMC clause) was
  // therefore drawn wider than measured and the next word overprinted it.
  // AIC/DELL carry the same tabs ("2.\t" before a bold title) where the
  // overrun only ate the tab's own blank space. Measure what is drawn: one
  // space per tab, so the clause reads "1. Contract" and nothing overlaps.
  const paragraphs = text.replace(/\t/g, ' ').split('\n');
  
  paragraphs.forEach((paragraph) => {
    if (!paragraph.trim()) {
      currentY += lineHeight; // Empty line spacing
      
      // Check if we need a new page
      if (currentY > effectiveMaxY) {
        addedPages = true;
        doc.addPage();
        currentY = 20; // Reset to top of new page with margin
      }
      return;
    }
    
    // Parse paragraph for **bold** markers
    const segments = [];
    let remainingText = paragraph;
    
    while (remainingText.length > 0) {
      const boldStart = remainingText.indexOf('**');
      
      if (boldStart === -1) {
        // No more bold text, add remaining as normal
        if (remainingText.trim()) {
          segments.push({ text: remainingText, bold: false });
        }
        break;
      }
      
      // Add text before bold marker as normal
      if (boldStart > 0) {
        segments.push({ text: remainingText.substring(0, boldStart), bold: false });
      }
      
      // Find closing bold marker
      const boldEnd = remainingText.indexOf('**', boldStart + 2);
      
      if (boldEnd === -1) {
        // No closing marker, treat rest as normal
        segments.push({ text: remainingText.substring(boldStart + 2), bold: false });
        break;
      }
      
      // Add bold text
      segments.push({ 
        text: remainingText.substring(boldStart + 2, boldEnd), 
        bold: true 
      });
      
      // Continue with remaining text
      remainingText = remainingText.substring(boldEnd + 2);
    }
    
    // Render segments with word wrapping
    let words = [];
    
    segments.forEach(segment => {
      const segmentWords = segment.text.split(' ').map(word => ({
        text: word,
        bold: segment.bold
      }));
      words.push(...segmentWords);
    });
    
    let currentLine = [];
    let currentLineWidth = 0;
    
    let currentMeasureFontStyle = null;

    words.forEach((word, index) => {
      const isLastWord = index === words.length - 1;
      const wordText = isLastWord ? word.text : word.text + ' ';
      
      // Set font to measure width - ONLY if style changed
      const fontStyle = word.bold ? "bold" : "normal";
      if (fontStyle !== currentMeasureFontStyle) {
        doc.setFont("helvetica", fontStyle);
        currentMeasureFontStyle = fontStyle;
      }
      const wordWidth = doc.getTextWidth(wordText);
      
      if (currentLineWidth + wordWidth > maxWidth && currentLine.length > 0) {
        // Check if we need a new page before rendering
        if (currentY + lineHeight > effectiveMaxY) {
          addedPages = true;
          doc.addPage();
          currentY = 20; // Reset to top of new page
        }
        
        // Render current line
        renderLine(doc, currentLine, startX, currentY);
        currentY += lineHeight;
        
        // Start new line
        currentLine = [{ text: wordText, bold: word.bold }];
        currentLineWidth = wordWidth;
      } else {
        // Add to current line
        currentLine.push({ text: wordText, bold: word.bold });
        currentLineWidth += wordWidth;
      }
    });
    
    // Render last line
    if (currentLine.length > 0) {
      // Check if we need a new page
      if (currentY + lineHeight > effectiveMaxY) {
        addedPages = true;
        doc.addPage();
        currentY = 20; // Reset to top of new page
      }
      
      renderLine(doc, currentLine, startX, currentY);
      currentY += lineHeight;
    }
  });
  
  return { finalY: currentY, addedPages }; // Return final Y position and whether we added pages
};

// Helper to render a single line with mixed formatting
const renderLine = (doc, segments, startX, y) => {
  let currentX = startX;
  let lastFontStyle = null; // Track the last font style used
  
  segments.forEach(segment => {
    // Only change font if it's different from the previous segment
    const fontStyle = segment.bold ? "bold" : "normal";
    if (fontStyle !== lastFontStyle) {
      doc.setFont("helvetica", fontStyle);
      lastFontStyle = fontStyle;
    }
    doc.text(segment.text, currentX, y);
    currentX += doc.getTextWidth(segment.text);
  });
};

/**
 * Draws the VDURA quote onto the CURRENT page of `doc` and any pages it needs
 * to add. Does not create, stamp, save, or upload the document — the caller
 * owns those, so this serves both the standalone quote (generateQuotePDF) and
 * the combined CONF-186 document.
 *
 * `options` (CONF-186): the two trailing booleans became an object because
 * eight positional parameters ending in two booleans cannot absorb three more
 * flags legibly. Every default below reproduces the pre-CONF-186 behaviour, so
 * the standard (non-CCI) quote path is provably untouched.
 */
export const renderVduraQuotePages = async (
  doc, metrics, bom, userName, config, pricingData, options = {}
) => {
  const {
    isFullQuote = true,
    showHwReference = false,
    termsMode = 'auto',      // 'auto' = T&Cs / disclaimer as today; 'pointer' = Ordering Instructions
    titles,                  // undefined | { title?: string, bandLabel?: string }
    suppressEmptyNotes = false,
  } = options;

  // Extract quote details from config
  const quoteDescription = config.quoteDescription || '';
  const notes = config.notes || '';

  const isBaseSystem = config.systemType === 'BaseSystem' ? true : false;

  let bomStartY;
  if(isFullQuote) {
    // For full quotes (no summary), start BOM Table after totals section
    bomStartY = 70;
  } else {
    // For system configuration summaries, start BOM Table after System Attributes Summary
    bomStartY = 110;
  }

  // Set default text color and font
  doc.setTextColor(0, 0, 0);
  doc.setFont("helvetica", "normal");

  // ========================================
  // LOGO AND HEADER SECTION
  // ========================================
  // NOTE: Optional other logo dimensions if it still looks squished (W:60, H:10, W: 70 H:10 - wider, W:50 H:8 - small but proportional)
  const logoWidth = 70;  // Width in mm
  const logoHeight = 10; // Height in mm  
  const logoX = 20;      // Left margin
  const logoY = 12;      // Top margin

  // Add the VDURA logo
  // doc.addImage(vduraLogoPng, 'PNG', logoX, logoY, logoWidth, logoHeight);
  const compressedLogo = await compressImage(vduraLogoPng, 800, 0.7);
  doc.addImage(compressedLogo, 'JPEG', logoX, logoY, logoWidth, logoHeight);

  // Add heading - different based on document type
  doc.setFontSize(16);
  // titles.title lets the combined document tell its lead section apart from
  // the executable software quote behind it; undefined keeps today's strings.
  const title = titles?.title || (isFullQuote ? 'Quotation' : 'VDURA V5000 System Configuration');
  // doc.text(title, doc.internal.pageSize.width / 2, 20, { align: 'center' });
  // doc.text(title, logoX + logoWidth+8, logoY + 8);
  // doc.text(title, logoX, logoY + logoHeight + 8);
  doc.text(title, doc.internal.pageSize.width / 2, logoY + logoHeight + 8, { align: 'center' });

  // ========== ADD ADDRESS BLOCK HERE ==========
  // Add VDURA address in top right corner
  doc.setFontSize(9); // Same size as "Prepared By" label
  doc.setFont(undefined, 'normal'); // Reset to normal font weight

  // Calculate right-aligned positions
  const pageWidth = doc.internal.pageSize.width;
  const rightMargin = 25;
  const addressX = pageWidth - rightMargin;

  // Address lines (right-aligned)
  doc.text('VDURA, Inc.', addressX, 12, { align: 'right' });
  doc.text('1501 Reedsdale Street Suite 400', addressX, 16, { align: 'right' });
  doc.text('Pittsburgh, PA 15233', addressX, 21, { align: 'right' });
  doc.text('www.vdura.com', addressX, 26, { align: 'right' });
  // ========== END ADDRESS BLOCK ==========


  doc.setFont("helvetica", "normal"); // reset font type
  // Quote Summary header
  doc.setFillColor(226, 226, 226);
  doc.rect(20, 35, doc.internal.pageSize.width - 40, 7, 'F');
  doc.setFontSize(11);
  const maxDescLen = 73;
  const truncatedDesc = quoteDescription && quoteDescription.length > maxDescLen
    ? quoteDescription.slice(0, maxDescLen) + '\u2026'
    : quoteDescription;
  const bandLabel = titles?.bandLabel || `${isFullQuote ? 'Quote' : 'System'} Summary`;
  doc.text(truncatedDesc ? `${bandLabel} - ${truncatedDesc}` : bandLabel, 25, 40);

  // Quote Information
  doc.setFontSize(9);
  const column1Y = 25;
  const column2Y = 60;
  const column3Y = 123;
  const column4Y = 155;

  const lineHeight = 4.5;
  const minRowSpacing = 6;
  const leftValMaxWidth = column3Y - column2Y - 3;
  const rightValMaxWidth = doc.internal.pageSize.width - 25 - column4Y;

  let currentY = 47;

  const renderSummaryRow = (leftLabel, leftValue, rightLabel, rightValue) => {
    const leftLines = doc.splitTextToSize(leftValue, leftValMaxWidth);
    const rightLines = rightLabel ? doc.splitTextToSize(rightValue, rightValMaxWidth) : [];
    doc.text(leftLabel, column1Y, currentY);
    doc.text(leftLines, column2Y, currentY);
    if (rightLabel) {
      doc.text(rightLabel, column3Y, currentY);
      doc.text(rightLines, column4Y, currentY);
    }
    const rowHeight = Math.max(leftLines.length, rightLines.length || 1) * lineHeight;
    currentY += Math.max(rowHeight, minRowSpacing);
  };

  // Row 1: Prepared By / Quote Number
  let combinedQuoteNumber = '';
  if (isFullQuote) {
    const serialQuoteNum = config.configuratorQuoteSerial || config.offerNumber || '';
    const salesforceNumber = config.salesforceQuoteNumber || '00000';
    combinedQuoteNumber = salesforceNumber ? `${serialQuoteNum}-${salesforceNumber}` : serialQuoteNum;
  }
  renderSummaryRow('Prepared By', config.accountExecutive || userName,
    isFullQuote ? 'Quote Number' : null, combinedQuoteNumber);

  // Row 2: Email / Quote Date
  const today = new Date();
  renderSummaryRow('Email', config.accountExecutiveEmail || userName,
    isFullQuote ? 'Quote Date' : null, isFullQuote ? formatDateLong(today) : '');

  // Row 3: Customer Name / Quote Expires
  const expiresText = (isFullQuote && config.quoteExpirationDate) ? formatDateLong(config.quoteExpirationDate) : '';
  renderSummaryRow('Customer Name', config.preparedFor || '',
    isFullQuote ? 'Quote Expires' : null, expiresText);

  // Conditional row for Partner Name and Warranty Start Date
  const hasPartnerName = config.partnerName && config.partnerName.trim() !== '';
  const hasWarrantyDate = config.warrantyStartDate && config.warrantyStartDate.trim() !== '';

  if ((hasPartnerName || hasWarrantyDate) && isFullQuote) {
    renderSummaryRow(
      hasPartnerName ? 'Partner Name' : '',
      hasPartnerName ? config.partnerName : '',
      hasWarrantyDate ? 'Warranty Start Date' : null,
      hasWarrantyDate ? formatDateLong(config.warrantyStartDate) : ''
    );
  }

  // ===== MODIFIED TOTALS SECTION - ALL ON SINGLE LINE =====
  // Summary cost box with all totals on one line
  doc.setFontSize(9);
  
  // Draw horizontal dividing line between Prepared For/Date and Totals
  doc.setDrawColor(128, 128, 128);  // Black color
  doc.setLineWidth(0.2);            // Thin line
  const lineY = currentY - 1;
  if (isFullQuote) {
    bomStartY = lineY + 7;
  }
  doc.line(20, lineY, doc.internal.pageSize.width - 20, lineY);                        // Draw line across the page width with margins
  
  // Calculate positions for the three columns
  const leftEdge = 25;
  const rightEdge = doc.internal.pageSize.width - 25;
  const columnWidth = (rightEdge - leftEdge) / 3;


  // ===== NEW SYSTEM ATTRIBUTES SUMMARY SECTION =====
  if(!isFullQuote) {
    const sectionStartY = lineY + 4;

    doc.setFillColor(226, 226, 226);
    doc.rect(20, sectionStartY, doc.internal.pageSize.width - 40, 7, 'F');
    doc.setFontSize(11);
    doc.text("System Attributes Summary", 25, sectionStartY+5);

    // Define base column positions
    const baseX = 25;
    const pageWidth = doc.internal.pageSize.width - 40;
    let attrY = sectionStartY+11;

    // Determine which storage types are active
    const hasAFA = config.afaEnabled && metrics.totalAfaRawCapacity > 0;
    const hasHybrid = config.hybridEnabled && metrics.totalHybridRawCapacity > 0;

    // Calculate column positions based on which storage types are active
    let positions;

    if (hasAFA && hasHybrid) {
      // All columns (4 columns)
      positions = [
        baseX,                    // System Attributes
        baseX + pageWidth * 0.25, // AFA Storage
        baseX + pageWidth * 0.5,  // Hybrid Storage
        baseX + pageWidth * 0.75  // Physical Attributes
      ];
    } else if (hasAFA && !hasHybrid) {
      // No Hybrid (3 columns)
      positions = [
        baseX,                    // System Attributes
        baseX + pageWidth * 0.33, // AFA Storage
        baseX + pageWidth * 0.67  // Physical Attributes
      ];
    } else if (!hasAFA && hasHybrid) {
      // No AFA (3 columns)
      positions = [
        baseX,                    // System Attributes
        baseX + pageWidth * 0.33, // Hybrid Storage
        baseX + pageWidth * 0.67  // Physical Attributes
      ];
    } else {
      // Neither AFA nor Hybrid (2 columns)
      positions = [
        baseX,                    // System Attributes
        baseX + pageWidth * 0.5   // Physical Attributes
      ];
    }

    // Column 1: System Attributes + Physical Attributes (always shown)
    const col1X = positions[0];
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.text("System Attributes", col1X, attrY);
    doc.setFont("helvetica", "normal");

    doc.text(`Raw: ${formatNumber(metrics.totalRawCapacity)} ${getCapacityUnit(config.isPBUnit, 'r')}`, col1X, attrY + 5);
    doc.text(`Useable: ${formatNumber(metrics.totalEffectiveCapacity)} ${getCapacityUnit(config.isPBUnit)}`, col1X, attrY + 9);
    doc.text(`Effective: ${formatNumber(metrics.totalCompressedEffectiveCapacity)} ${getCapacityUnit(config.isPBUnit, 'e')}`, col1X, attrY + 13);
    doc.text(`Shipment: ${config.quarter || ''}`, col1X, attrY + 17);

    // Physical Attributes sub-section below System Attributes in col-0
    doc.setFont("helvetica", "bold");
    doc.text("Physical Attributes", col1X, attrY + 23);
    doc.setFont("helvetica", "normal");
    doc.text(`Rack Units: ${formatNumber(metrics.totalRU)} RU`, col1X, attrY + 28);
    doc.text(`Racks: ${Math.ceil(metrics.totalRU / 42)}`, col1X, attrY + 32);
    const maxPowerStr = metrics.totalMaxPower > 0
      ? formatNumber(metrics.totalMaxPower / 1000, 1)
      : 'N/A';
    doc.text(`Power (Typ/Max): ${formatNumber(metrics.totalNominalPower / 1000, 1)}/${maxPowerStr} kW`, col1X, attrY + 36);
    doc.text(`Weight: ${formatNumber(metrics.totalWeight)} kg`, col1X, attrY + 40);

    // CONF-93: the quote did not state the network at all before this. Label
    // wording matches buildNetworkHardwareSummaryRows (rackSlidePPTGenerator,
    // Task 11) so the slide and this PDF describe the same quote the same way.
    const capabilities = getVendorCapabilities(config.hardwareVendor);
    const networkOption = getNetworkOption(capabilities, config.interfaceOption);
    const networkPorts = calculateNetworkPorts(config, capabilities);
    doc.text(`Network: ${networkOption?.label ?? config.interfaceOption}`, col1X, attrY + 44);
    doc.text(`Protocol: ${config.networkProtocol || 'TCP'}`, col1X, attrY + 48);
    doc.text(`Network Ports: ${networkPorts.totalPorts}`, col1X, attrY + 52);

    // Column index tracker for determining remaining columns
    let colIndex = 1;

    // Column 2: AFA Storage Set (if enabled)
    if (hasAFA) {
      const colX = positions[colIndex++];
      doc.setFont("helvetica", "bold");
      doc.text("AFA Storage Set", colX, attrY);
      doc.setFont("helvetica", "normal");

      // Always display RAW, will add more metrics in future for non-base systems
      doc.text(`Raw: ${formatNumber(metrics.totalAfaRawCapacity)} ${getCapacityUnit(config.isPBUnit, 'r')}`, colX, attrY + 5);
      doc.text(`Useable: ${formatNumber(metrics.afaEffectiveCapacity)} ${getCapacityUnit(config.isPBUnit)}`, colX, attrY + 9);
      doc.text(`Effective: ${formatNumber(metrics.afaCompressedEffectiveCapacity)} ${getCapacityUnit(config.isPBUnit, 'e')}`, colX, attrY + 13);
      if (isBaseSystem) {
        // CONF-125: IOPs hidden — values stale, will restore when updated data is available
        // doc.text(`IOPS: Up to ${formatNumber(metrics.afaIops, 1)}M/s`, colX, attrY + 17);
        doc.text(`Sustained: Up to ${formatNumber(metrics.afaReadTransferRate)} / ${formatNumber(metrics.afaWriteTransferRate)} ${getThroughputUnit(config.isPBUnit)}`, colX, attrY + 21);
        doc.text(`VPOD Spares: ${formatNumber(config.afaSpares)}`, colX, attrY + 25);
        doc.text(`(@ ${config.afaCompressionRatio}:1 reduction)`, colX, attrY + 29);
        doc.text(`Encryption : ${config.afaEndToEndEncryption ? 'Enabled' : 'Disabled'}`, colX, attrY + 33);
      }
    }

    // Column: Mixed Fleet Storage Set (if enabled)
    if (hasHybrid) {
      const colX = positions[colIndex++];
      doc.setFont("helvetica", "bold");
      doc.text("Mixed Fleet Storage Set", colX, attrY);
      doc.setFont("helvetica", "normal");

      // Always display RAW, will add more metrics in future for non-base systems
      doc.text(`Raw: ${formatNumber(metrics.totalHybridRawCapacity)} ${getCapacityUnit(config.isPBUnit, 'r')} (${formatNumber(metrics.totalHybridSsdCapacity)} ${getCapacityUnit(config.isPBUnit)} NVMe)`, colX, attrY + 5);
      doc.text(`Useable: ${formatNumber(metrics.hybridEffectiveCapacity)} ${getCapacityUnit(config.isPBUnit)}`, colX, attrY + 9);
      doc.text(`Effective: ${formatNumber(metrics.hybridCompressedEffectiveCapacity)} ${getCapacityUnit(config.isPBUnit, 'e')}`, colX, attrY + 13);
      if (isBaseSystem) {
        // CONF-125: IOPs hidden — values stale, will restore when updated data is available
        // doc.text(`IOPS: Up to ${formatNumber(metrics.hybridIops, 1)}M/s`, colX, attrY + 17);
        doc.text(`Burst: Up to ${formatNumber(metrics.hybridBurstReadTransferRate)} / ${formatNumber(metrics.hybridBurstWriteTransferRate)} ${getThroughputUnit(config.isPBUnit)}`, colX, attrY + 21);
        doc.text(`Sustained: Up to ${formatNumber(metrics.hybridReadTransferRate)} / ${formatNumber(metrics.hybridWriteTransferRate)} ${getThroughputUnit(config.isPBUnit)}`, colX, attrY + 25);
        doc.text(`VPOD Spares: ${formatNumber(config.hybridSpares)}`, colX, attrY + 29);
        doc.text(`(@ ${config.hybridCompressionRatio}:1 reduction)`, colX, attrY + 33);
        doc.text(`Encryption : ${config.hybridEndToEndEncryption ? 'Enabled' : 'Disabled'}`, colX, attrY + 37);
        doc.text(`MLEC : ${config.hybridMLEC ? 'Enabled' : 'Disabled'}`, colX, attrY + 41);
      }
    }

    // col-0 always ends at Physical Attributes (fixed height: header at +23,
    // last row — Network Ports, CONF-93 — at +52)
    const col0Height = 57;

    // Each arm tracks its column's last row by convention (hybrid: MLEC at
    // +41, CONF-94; AFA: Encryption at +33). col0Height (57) currently
    // dominates maxAttrOffset for every arm, so this value is a margin
    // against future column growth rather than today's binding constraint.
    const afaHybridColHeight = isBaseSystem ? (hasHybrid ? 41 : hasAFA ? 33 : 17) : 17;
    const maxAttrOffset = Math.max(col0Height, afaHybridColHeight);
    bomStartY = attrY + maxAttrOffset + 8;
  }

  // Don't print anything that isn't displayed in the UI
  // MORK TODO : This isn't a great solution but gets us moving right now. 
  const visibleBom = bom.filter(item => item.showInUi !== false);

  // Split the BOM into software and hardware items
  const softwareItems = [];
  const hardwareItems = [];

  // These part numbers are typically software items
  const softwarePartPatterns = ['VDP-', 'HW-Support-', 'SVC-'];

  visibleBom.forEach(item => {
    if (!item.partNumber) return;

    let isSoftware = false;
    for (const pattern of softwarePartPatterns) {
      if (item.partNumber && item.partNumber.startsWith(pattern)) {
        isSoftware = true;
        break;
      }
    }

    if (isSoftware) {
      softwareItems.push(item);
    } else if (item.partNumber && item.partNumber.startsWith('VCH-')) {
      hardwareItems.push(item);
    }
  });

  // For vendors where hardware is fulfilled by the partner, only software subscriptions appear in the quote
  const softwareOnly = isSoftwareSubscriptionOnly(config.hardwareVendor);
  
  // Software Section - FULFILLED BY VDURA
  const softwareY = bomStartY; // Moved up to make document more compact
  
  // Calculate table width
  const tableWidth = doc.internal.pageSize.width - 40;
  
  // Yellow header for software section - match table width exactly
  doc.setFillColor(244, 167, 40); // Yellow/orange
  doc.rect(20, softwareY, tableWidth, 7, 'F');
  doc.setFontSize(10);
  doc.setTextColor(0, 0, 0);
  doc.text('SOFTWARE AND SUPPORT: FULFILLED BY VDURA', doc.internal.pageSize.width / 2, softwareY + 5, { align: 'center' });

  // Software table with Node column added
  const softwareColumns = [
    'PART NO.',
    'DESCRIPTION', 
    'QTY',
    'MONTHS',
    'LIST PRICE',
    'DISCOUNT %',
    'DISCOUNTED PRICE',
    'EXTENDED PRICE'
  ];

  // CONF-229: discount off list, per unit. Blank when there is no list price
  // to discount from, rather than a misleading 0% or 100%.
  const discountPercent = (item) => {
    const list = parseFloat(item.list);
    if (!(list > 0)) return '';
    const unit = parseFloat(item.unitCost) || 0;
    return `${(((list - unit) / list) * 100).toFixed(1)}%`;
  };

  const softwareData = softwareItems.map(item => [
    item.partNumber || '',
    item.item || '',
    item.quantity || '',
    item.months || '',
    item.list ? `$${formatCurrency(parseFloat(item.list))}` : '',
    discountPercent(item),
    item.unitCost ? `$${formatCurrency(parseFloat(item.unitCost))}` : '',
    item.totalCost ? `$${formatCurrency(parseFloat(item.totalCost))}` : ''
  ]);

  // Add software table - make it even more compact to fit on one page
  const softwareTableY = softwareY + 8;
  doc.autoTable({
    startY: softwareTableY,
    head: [softwareColumns],
    body: softwareData,
    theme: 'grid',
    styles: { fontSize: 6, cellPadding: 1 }, // Even smaller font and padding to fit more
    headStyles: { fillColor: [119, 119, 119], textColor: [255, 255, 255] },
    columnStyles: {
      0: { cellWidth: 38 }, // PART NO - narrower
      1: { cellWidth: 'auto' }, // DESCRIPTION
      2: { cellWidth: 10, halign: 'center' }, // QTY - narrower
      3: { cellWidth: 12, halign: 'center' }, // MONTHS - narrower
      4: { cellWidth: 18, halign: 'right' }, // LIST PRICE - narrower
      5: { cellWidth: 14, halign: 'right' }, // DISCOUNT %
      6: { cellWidth: 18, halign: 'right' }, // DISCOUNTED PRICE - narrower
      7: { cellWidth: 20, halign: 'right' } // EXTENDED PRICE - narrower
    },
    margin: { left: 20, right: 20 }, // Ensure margins match section header
    foot: (softwareOnly || showHwReference)
      ? [
          [{ content: 'Total Software & Support Cost', colSpan: 7, styles: { halign: 'right', fontStyle: 'bold' } }, `$${formatCurrency(metrics.totalSoftwareCost)}`],
          [{ content: 'Quote Cost Total', colSpan: 7, styles: { halign: 'right', fontStyle: 'bold' } }, `$${formatCurrency(metrics.totalSoftwareCost)}`]
        ]
      : [
          [{ content: 'Total Software & Support Cost', colSpan: 7, styles: { halign: 'right', fontStyle: 'bold' } }, `$${formatCurrency(metrics.totalSoftwareCost)}`]
        ],
    footStyles: { fontStyle: 'bold', halign: 'right', fillColor: [240, 240, 240], textColor: [0, 0, 0] }
  });

  // Hardware Section - only shown when vendor includes hardware in the quote (and not overridden to show HW reference instead)
  if (!softwareOnly && !showHwReference && hardwareItems.length > 0) {
    const softwareTableEndY = doc.lastAutoTable.finalY;
    const hardwareY = softwareTableEndY + 5;

    // Yellow header for hardware section - match table width exactly
    doc.setFillColor(244, 167, 40); // Yellow/orange
    doc.rect(20, hardwareY, tableWidth, 7, 'F');
    doc.setFontSize(10);
    doc.text('COMMODITY HARDWARE ESTIMATE: FULFILLED BY CONTRACT MANUFACTURER', doc.internal.pageSize.width / 2, hardwareY + 5, { align: 'center' });

    // Hardware table with Node column added
    const hardwareColumns = [
      'PART NO.',
      'DESCRIPTION',
      'QTY',
      'MONTHS',
      'LIST PRICE',
      'DISCOUNTED PRICE',
      'EXTENDED PRICE'
    ];

    const hardwareData = hardwareItems.map(item => [
      item.partNumber || '',
      item.item || '',
      item.quantity || '',
      item.months || '',
      item.list ? `$${formatCurrency(parseFloat(item.list))}` : '',
      item.unitCost ? `$${formatCurrency(parseFloat(item.unitCost))}` : '',
      item.totalCost ? `$${formatCurrency(parseFloat(item.totalCost))}` : ''
    ]);

    const hardwareTableY = hardwareY + 8;
    doc.autoTable({
      startY: hardwareTableY,
      head: [hardwareColumns],
      body: hardwareData,
      theme: 'grid',
      styles: { fontSize: 6, cellPadding: 1 },
      headStyles: { fillColor: [119, 119, 119], textColor: [255, 255, 255] },
      columnStyles: {
        0: { cellWidth: 38 },
        1: { cellWidth: 'auto' },
        2: { cellWidth: 10, halign: 'center' },
        3: { cellWidth: 12, halign: 'center' },
        4: { cellWidth: 18, halign: 'right' },
        5: { cellWidth: 18, halign: 'right' },
        6: { cellWidth: 20, halign: 'right' }
      },
      margin: { left: 20, right: 20 },
      foot: [
        [{ content: 'Total Hardware Cost', colSpan: 6, styles: { halign: 'right', fontStyle: 'bold' } }, `$${formatCurrency(metrics.totalHardwareCost)}`],
        [{ content: 'Quote Cost Total', colSpan: 6, styles: { halign: 'right', fontStyle: 'bold' } }, `$${formatCurrency(metrics.totalSolutionCost)}`]
      ],
      footStyles: { fontStyle: 'bold', halign: 'right', fillColor: [240, 240, 240], textColor: [0, 0, 0] }
    });
  }

  // Alternate Hardware Reference Section - for SW-only vendors (e.g. Dell) and CCI SW-only path
  if (softwareOnly || showHwReference) {
    const vendorDisplayName = getVendor(config.hardwareVendor)?.displayName || config.hardwareVendor;
    const hwRefY = doc.lastAutoTable.finalY + 5;

    doc.setFillColor(244, 167, 40); // Orange to match other section banners
    doc.rect(20, hwRefY, tableWidth, 7, 'F');
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    // Banner is deliberately vendor-agnostic — quotes must not name the manufacturer.
    doc.text('HARDWARE REFERENCE: FULFILLED BY CONTRACT MANUFACTURER', doc.internal.pageSize.width / 2, hwRefY + 5, { align: 'center' });

    // Build rows: Director always, AFA if enabled, Hybrid SSD + JBOD if hybrid enabled.
    // Shared with the on-screen Hardware Reference card (App.js) so screen and PDF
    // cannot drift.
    const hwRefRows = buildHardwareReferenceRows(config, metrics, vendorDisplayName).map(row => ([
      row.nodeDescription,
      row.nodeQty,
      row.deviceCapacityTB,
      row.deviceQty,
      formatNumber(row.totalRawTB, 2)
    ]));

    doc.autoTable({
      startY: hwRefY + 8,
      head: [['Node Description', 'Node Qty', 'Storage Device Capacity (TB)', 'Storage Device Qty', 'Total Raw Capacity (TB)']],
      body: hwRefRows,
      theme: 'grid',
      styles: { fontSize: 7, cellPadding: 1.5 },
      headStyles: { fillColor: [119, 119, 119], textColor: [255, 255, 255] },
      columnStyles: {
        0: { cellWidth: 'auto' },
        1: { cellWidth: 20, halign: 'center' },
        2: { cellWidth: 40, halign: 'right' },
        3: { cellWidth: 30, halign: 'center' },
        4: { cellWidth: 40, halign: 'right' }
      },
      margin: { left: 20, right: 20 }
    });
  }

  // Notes Section
  const notesY = doc.lastAutoTable.finalY + 3;

  // CONF-186: the combined document's lead page drops the whole block when the
  // AE typed nothing, rather than printing a bare header over blank space. The
  // standard path still prints the empty header, so nothing changes there.
  const showNotes = !suppressEmptyNotes || notes.trim() !== '';

  // Height the notes block consumes, including its trailing 4mm gap. Zero when
  // suppressed, so the terms block takes the space the header would have used.
  let notesBlockHeight = 0;

  if (showNotes) {
    // Gray header for notes - match table width
    doc.setFillColor(226, 226, 226);
    doc.rect(20, notesY, tableWidth, 7, 'F');
    doc.setFontSize(10);
    doc.text('Notes', 25, notesY + 5);

    // Notes content - wrap to fit within page width
    doc.setFontSize(9);
    const noteLines = notes ? doc.splitTextToSize(notes, tableWidth - 10) : [];
    if (noteLines.length > 0) {
      doc.text(noteLines, 25, notesY + 13);
    }
    const notesTextHeight = Math.max(noteLines.length, 1) * 4.5;
    notesBlockHeight = 13 + notesTextHeight + 4;
  }

  // Terms and Conditions or NOT VALID QUOTATION based on document type
  const pageHeight = doc.internal.pageSize.height;
  const bottomMargin = 15;
  let termsY = notesY + notesBlockHeight;

  // If the terms/disclaimer header would fall off the current page, start a new one
  if (termsY + 7 > pageHeight - bottomMargin) {
    doc.addPage();
    termsY = 20;
  }

  // Gray header for terms or disclaimer - match table width
  doc.setFillColor(226, 226, 226);
  doc.rect(20, termsY, tableWidth, 7, 'F');
  doc.setFontSize(10);
  
  if (termsMode === 'pointer') {
    // CONF-186: the lead page of the combined document carries prices and a
    // total but no T&Cs, so it says plainly what it is and where the executable
    // instruments are. Deliberately vendor-agnostic — see the banner note above.
    doc.text('Ordering Instructions', 25, termsY + 5);

    doc.setFontSize(9);
    const pointerText = 'This page summarizes the complete solution and total price for reference. '
      + 'The two quotations that follow are the executable documents: Hardware is ordered from the '
      + 'contract manufacturer under the Hardware Quotation. Software subscription and support are '
      + 'ordered from VDURA, Inc. under the Software & Support Quotation.';
    doc.text(doc.splitTextToSize(pointerText, tableWidth - 10), 25, termsY + 13);
  } else if (isFullQuote) {
    const termsConfig = getVendorTerms(config.hardwareVendor);

    // For full quotes, show Terms and Conditions
    doc.text(termsConfig.title, 25, termsY + 5);

    // Terms content
    doc.setFontSize(7); // Small font for terms
    const termsText = termsConfig.content;
    const orderText = termsConfig.orderText;
    
    // NEW CODE - Using markdown parser with proper pagination
    const startX = 25; // Text starts at X position 25 (5mm from left margin)
    const maxWidth = tableWidth - 10; // Max width accounting for 5mm padding on each side (tableWidth already accounts for 20mm margins on each side)
    const startY = termsY + 13;
    const pageHeight = doc.internal.pageSize.height;
    const bottomMargin = 15;

    doc.setFontSize(7);

    // The page the terms section actually begins on. Previously this loop
    // hardcoded page 2, which mislabelled any earlier page — a BOM that spilled,
    // and (CONF-186) the summary and CCI pages now sitting in front of us.
    const termsStartPage = doc.internal.getCurrentPageInfo().pageNumber;

    // Render with pagination awareness
    const result = renderFormattedText(doc, termsText, startX, startY, maxWidth, pageHeight - bottomMargin);
    let addedPages = result.addedPages;

    // CONF-216: the order text (SMC's is the only non-empty one) used to be
    // drawn unwrapped at a fixed 5mm under the last line, with no page check,
    // so it could run past the right margin, into the footer or off the page.
    // It now flows through the same wrap-and-paginate path as the terms.
    if (orderText) {
      doc.setFontSize(7);
      doc.setFont("helvetica", "normal");
      const order = renderFormattedText(doc, orderText, startX, result.finalY + 5, maxWidth, pageHeight - bottomMargin);
      addedPages = addedPages || order.addedPages;
    }

    // If we added pages, add proper headers and continuation text
    if (addedPages) {
      const totalPages = doc.internal.getNumberOfPages();
      for (let i = termsStartPage + 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFillColor(226, 226, 226);
        doc.rect(20, 10, tableWidth, 7, 'F');
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text('Terms and Conditions (continued)', 25, 15);
      }
      doc.setPage(totalPages);
    }
    // // Need to split the lengthy terms text into lines that fit the PDF width
    // const splitTerms = doc.splitTextToSize(termsText, 170);
    
    // // Calculate available height on current page and how many lines will fit
    // const lineHeight = 3.5; // Approximate height of each line in mm
    // const availableHeight = doc.internal.pageSize.height - termsY - 20; // Available space on current page
    // const maxLinesOnFirstPage = Math.floor(availableHeight / lineHeight);
    
    // // Check if we need multiple pages for terms
    // if (splitTerms.length > maxLinesOnFirstPage) {
    //   // First, show as many lines as fit on the first page
    //   const firstPageTerms = splitTerms.slice(0, maxLinesOnFirstPage);
    //   doc.text(firstPageTerms, 25, termsY + 13);

    //   // Add a new page for the remaining terms
    //   doc.addPage();
      
    //   // Add header to the new page
    //   doc.setFillColor(226, 226, 226);
    //   doc.rect(20, 20, tableWidth, 7, 'F');
    //   doc.setFontSize(10);
    //   doc.text('Terms and Conditions (continued)', 25, 25);
      
    //   // Show remaining terms on the new page
    //   const remainingTerms = splitTerms.slice(maxLinesOnFirstPage);
    //   doc.setFontSize(7);
    //   doc.text(remainingTerms, 25, 35);
      
    //   // Add the order text at the bottom of the terms on the second page
    //   const remainingTermsHeight = remainingTerms.length * lineHeight;
    //   const orderTextY = Math.min(35 + remainingTermsHeight + 5, doc.internal.pageSize.height - 15);
    //   doc.text(orderText, 25, orderTextY);
    // } else {
    //   // If everything fits on the current page
    //   doc.text(splitTerms, 25, termsY + 13);
    //   doc.text(orderText, 25, termsY + 13 + (splitTerms.length * lineHeight) + 5);
    // }
  } else {
    // For system configuration summaries, show disclaimer
    doc.text('Disclaimer', 25, termsY + 5);
    
    // Disclaimer content
    doc.setFontSize(9); // Slightly larger for emphasis
    doc.setTextColor(255, 0, 0); // Red text for emphasis
    doc.text('NOT A VALID QUOTATION', doc.internal.pageSize.width / 2, termsY + 15, { align: 'center' });
    doc.setTextColor(0, 0, 0); // Reset to black
    doc.setFontSize(7);
    doc.text('This document is for reference purposes only and does not constitute an offer for sale.', 25, termsY + 20);
    doc.text('Please contact your VDURA representative for official quotation.', 25, termsY + 23);
  }
};


export const buildVduraQuoteFilename = (config, isFullQuote) => {
  if (!isFullQuote) return `VDURA_V5000_System_Configuration_${Date.now()}.pdf`;
  // CONF-214: the serial arrives as "#1234" and the customer is free text.
  const customerName = sanitizeFilenamePart(config.preparedFor, 'Customer');
  const quoteSerial = sanitizeFilenamePart(config.quoteSerial || config.offerNumber, 'QUOTE');
  return `VDURA_V5000_Quotation_${customerName}_${quoteSerial}.pdf`;
};

/**
 * Standalone VDURA quote — one A4 PDF, downloaded, with a copy pushed to the
 * external quote-explorer bucket. Unchanged signature: three App.js call sites
 * depend on it, and CONF-186 keeps it as the fallback when the combined
 * document cannot be built.
 */
export const generateQuotePDF = async (
  metrics, bom, userName, config, pricingData, isFullQuote = true, showHwReference = false
) => {
  const doc = new jsPDF({
    compress: true,
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
    encryption: {
      userPassword: null,
      ownerPassword: null,
      userPermissions: ['print', 'copy', 'annot-forms'],
    },
  });

  await renderVduraQuotePages(doc, metrics, bom, userName, config, pricingData, { isFullQuote, showHwReference });
  stampPageNumbers(doc, VDURA_FOOTER);

  const filename = buildVduraQuoteFilename(config, isFullQuote);
  doc.save(filename);

  try {
    await uploadToExternalS3(filename, doc.output('blob'), 'application/pdf');
  } catch (err) {
    console.error('External S3 PDF upload failed:', err);
  }
};

// Helper function to format currency
const formatCurrency = (value) => {
  if (isNaN(value)) return "0.00";
  
  return value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

// Helper function to format numbers with commas and optional decimals
const formatNumber = (value, decimals = 0) => {
  if (isNaN(value)) return "0";
  
  return value.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
};

export default generateQuotePDF;